import { ComponentType, ReactNode } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, RouteProps } from 'react-router';
import { Navigate } from 'react-router-dom-v5-compat';

import { SentryRoute, SentryRouteV6 } from 'dashboard/components/SentryRoute/SentryRoute';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { RootState } from 'dashboard/redux/store';

interface AuthenticatedRouteProps extends RouteProps {
  component: ComponentType<RouteComponentProps<any>> | ComponentType<any>;
  path: string;

  /**
   * Page title to display in browser. Can be overridden using react-helmet.
   */
  title: string;
  redirectOnMissingCurrentEnvironment?: boolean;

  /**
   * Whether or not to render as react-router v6
   */
  v6?: boolean;
}

type RenderComponent = (props: RouteComponentProps<any>) => ReactNode;

const AuthenticatedRoute = ({
  component: Component,
  currentEnvironment,
  location,
  redirectOnMissingCurrentEnvironment = true,
  title,
  v6,
  ...rest
}: AuthenticatedRouteProps & PropsFromRedux) => {
  const renderComponent: RenderComponent = (props) => {
    if (SessionClientDashboard.isFetchingSessionToken) {
      // When the dashboard client is in this state, we reload
      // the page. So we don't need to set the flag back to
      // true after fetching the token.
      return <div />;
    } else if (!SessionClientDashboard.isLoggedIn()) {
      return <Navigate to='/dashboard/login' state={{ from: location?.pathname }} replace />;
    } else if (SessionClientDashboard.hasExpired()) {
      SessionClientDashboard.handleExpiration();
      return <div />;
    } else if (redirectOnMissingCurrentEnvironment && currentEnvironment == null) {
      return <Navigate to='/dashboard/home' state={{ from: location?.pathname }} replace />;
    }

    return <Component {...props} />;
  };

  return v6 ? (
    <SentryRouteV6 {...rest} render={renderComponent} title={title} />
  ) : (
    <SentryRoute {...rest} render={renderComponent} title={title} />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
  };
};

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AuthenticatedRoute);
